import React from 'react';
import { motion } from 'framer-motion';
import Section from '../layout/Section';
import Button from '../ui/Button';

interface CallToActionProps {
  /**
   * CTA title
   */
  title: string;
  /**
   * CTA description or subtitle
   */
  description?: string;
  /**
   * Primary button text
   */
  primaryBtnText?: string;
  /**
   * Primary button URL
   */
  primaryBtnUrl?: string;
  /**
   * Secondary button text
   */
  secondaryBtnText?: string;
  /**
   * Secondary button URL
   */
  secondaryBtnUrl?: string;
  /**
   * Background color class
   */
  bgColor?: string;
  /**
   * Text color class for content
   */
  textColor?: string;
  /**
   * Alternative property names
   */
  ctaText?: string;
  ctaLink?: string;
}

/**
 * Call to action section component
 */
const CallToAction: React.FC<CallToActionProps> = ({
  title,
  description,
  primaryBtnText,
  primaryBtnUrl,
  secondaryBtnText,
  secondaryBtnUrl,
  bgColor = 'bg-neu-bg',
  textColor = 'text-neu-text',
  ctaText,
  ctaLink,
}) => {
  // Support both naming conventions
  const buttonText = primaryBtnText || ctaText;
  const buttonUrl = primaryBtnUrl || ctaLink;
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    },
  };

  return (
    <Section
      bgColor={bgColor}
      padding="lg"
      className="overflow-hidden relative"
    >
      <motion.div
        className="text-center max-w-3xl mx-auto relative z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.h2
          className={`text-3xl md:text-4xl font-bold mb-4 ${textColor}`}
          variants={itemVariants}
        >
          {title}
        </motion.h2>

        {description && (
          <motion.p
            className={`text-lg mb-8 text-neu-muted`}
            variants={itemVariants}
          >
            {description}
          </motion.p>
        )}

        <motion.div
          className="flex flex-col sm:flex-row justify-center gap-4"
          variants={itemVariants}
        >
          <Button
            variant="accent"
            size="lg"
            onClick={() => { window.location.href = buttonUrl || '/'; }}
            className="font-semibold"
          >
            {buttonText || 'Get Started'}
          </Button>

          {secondaryBtnText && secondaryBtnUrl && (
            <Button
              variant="secondary"
              size="lg"
              onClick={() => { window.location.href = secondaryBtnUrl; }}
              className="font-semibold"
            >
              {secondaryBtnText}
            </Button>
          )}
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default CallToAction;